import React from 'react';
import { DbsCheckStatus } from '../../services/api';

interface BadgeConfig {
    label: string;
    badgeClass: string;
    dotClass: string;
    textClass: string;
}

function getConfig(status: DbsCheckStatus | undefined, clear?: boolean | null): BadgeConfig {
    if (status === 'app_complete') {
        return clear === true
            ? { label: 'Clear', badgeClass: 'bg-green-100 text-green-800 border-green-200', dotClass: 'bg-green-500', textClass: 'text-[#4a8a1c]' }
            : { label: 'Not Clear', badgeClass: 'bg-red-100 text-red-800 border-red-200', dotClass: 'bg-red-500', textClass: 'text-red-600' };
    }

    switch (status) {
        case 'link_sent':
            return { label: 'Invite Sent', badgeClass: 'bg-blue-100 text-blue-800 border-blue-200', dotClass: 'bg-blue-400', textClass: 'text-blue-700' };
        case 'awaiting_initiation':
            return { label: 'Action Required', badgeClass: 'bg-amber-100 text-amber-800 border-amber-200', dotClass: 'bg-amber-500', textClass: 'text-amber-700' };
        case 'form_ready':
            return { label: 'Submitted', badgeClass: 'bg-blue-100 text-blue-800 border-blue-200', dotClass: 'bg-blue-500', textClass: 'text-blue-700' };
        case 'form_invalid':
            return { label: 'Form Invalid', badgeClass: 'bg-red-100 text-red-800 border-red-200', dotClass: 'bg-red-500', textClass: 'text-red-600' };
        case 'form_complete':
            return { label: 'Awaiting Countersign', badgeClass: 'bg-blue-100 text-blue-800 border-blue-200', dotClass: 'bg-blue-500', textClass: 'text-blue-700' };
        case 'form_authorised':
            return { label: 'Countersigned', badgeClass: 'bg-blue-100 text-blue-800 border-blue-200', dotClass: 'bg-blue-600', textClass: 'text-blue-700' };
        case 'app_sent':
            return { label: 'Sent to DBS', badgeClass: 'bg-indigo-100 text-indigo-800 border-indigo-200', dotClass: 'bg-indigo-500', textClass: 'text-indigo-700' };
        case 'app_received':
            return { label: 'Received by DBS', badgeClass: 'bg-indigo-100 text-indigo-800 border-indigo-200', dotClass: 'bg-indigo-600', textClass: 'text-indigo-700' };
        case 'app_rejected':
            return { label: 'Rejected', badgeClass: 'bg-red-100 text-red-800 border-red-200', dotClass: 'bg-red-600', textClass: 'text-red-600' };
        case 'app_withdrawn':
            return { label: 'Withdrawn', badgeClass: 'bg-red-100 text-red-800 border-red-200', dotClass: 'bg-red-600', textClass: 'text-red-600' };
        default:
            return { label: 'Not Requested', badgeClass: 'bg-gray-100 text-gray-600 border-gray-200', dotClass: 'bg-gray-300', textClass: 'text-gray-500' };
    }
}

interface DbsStatusBadgeProps {
    status?: DbsCheckStatus;
    clear?: boolean | null;
    variant?: 'badge' | 'dot' | 'inline';
}

const DbsStatusBadge: React.FC<DbsStatusBadgeProps> = ({ status, clear, variant = 'badge' }) => {
    const config = getConfig(status, clear);

    if (variant === 'dot') {
        return (
            <span
                className={`inline-block w-3 h-3 rounded-full ${config.dotClass}`}
                title={config.label}
            />
        );
    }

    if (variant === 'inline') {
        return (
            <span className={`inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold ${config.textClass}`}>
                <span className={`h-4 w-[3px] flex-none rounded-full ${config.dotClass}`} aria-hidden="true" />
                {config.label}
            </span>
        );
    }

    return (
        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full border ${config.badgeClass}`}>
            {config.label}
        </span>
    );
};

export default DbsStatusBadge;
